import React, { useState, useMemo } from "react";
import moment from "moment";
import clsx from "clsx";
import { Participant } from "../models/Participant";
import { VisitationRotationBlock } from "../models/VisitationRotationBlock";
import { Button } from "../design/button";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/20/solid";

interface CustodyTimelineProps {
  rotation: {
    [key: number]: VisitationRotationBlock;
  };
  startDate: string;
  endDate?: string;
  participants: Participant[];
}

export const CustodyTimeline: React.FC<CustodyTimelineProps> = ({
  rotation,
  startDate,
  endDate,
  participants,
}) => {
  const [monthOffset, setMonthOffset] = useState(0);

  const scheduleStart = moment(startDate).startOf("day");
  const viewMonth = useMemo(
    () => scheduleStart.clone().add(monthOffset, "months").startOf("month"),
    [scheduleStart, monthOffset],
  );
  const scheduleEnd = endDate
    ? moment(endDate).endOf("day")
    : moment(viewMonth).endOf("month");

  const getParticipantInitials = (id: string | undefined) =>
    participants.find((p) => p._id === id)?.initials ?? "—";

  const rotationKeys = Object.keys(rotation)
    .map(Number)
    .sort((a, b) => a - b);
  const rotationLength = rotationKeys.length;

  type TimelineEntry = {
    date: moment.Moment;
    participantId?: string;
    time?: string;
  };

  const daysInMonth = viewMonth.daysInMonth();
  const timelineData: TimelineEntry[] = Array.from(
    { length: daysInMonth },
    (_, i) => {
      const date = viewMonth.clone().add(i, "days");
      const dayIndex = date.diff(scheduleStart, "days");

      const inSchedule =
        date.isSameOrAfter(scheduleStart) && date.isSameOrBefore(scheduleEnd);

      const rotationIndex = dayIndex % rotationLength;
      const block = rotation[rotationIndex];
      const current = Array.isArray(block) ? block[0] : (block ?? {});

      return {
        date,
        participantId: inSchedule ? current.participantId : undefined,
        time: inSchedule ? current.time : undefined,
      };
    },
  );

  // Group into weeks
  const weeks: TimelineEntry[][] = [];
  let week: TimelineEntry[] = [];

  const firstDayOfWeek = timelineData[0].date.clone().startOf("week");
  const padStart = timelineData[0].date.diff(firstDayOfWeek, "days");

  for (let i = 0; i < padStart; i++) {
    week.push({
      date: firstDayOfWeek.clone().add(i, "days"),
      participantId: undefined,
      time: undefined,
    });
  }

  timelineData.forEach((entry) => {
    week.push(entry);
    if (week.length === 7) {
      weeks.push(week);
      week = [];
    }
  });

  if (week.length) {
    while (week.length < 7) {
      const lastDate = week[week.length - 1].date;
      week.push({
        date: lastDate.clone().add(1, "day"),
        participantId: undefined,
        time: undefined,
      });
    }
    weeks.push(week);
  }

  const isWithinSchedule = (date: moment.Moment) =>
    date.isSameOrAfter(scheduleStart) && date.isSameOrBefore(scheduleEnd);

  const participantColorMap = useMemo(() => {
    const palette = [
      "bg-blue-400",
      "bg-pink-400",
      "bg-green-400",
      "bg-purple-400",
    ];
    return participants.reduce(
      (acc, p, i) => {
        acc[p._id] = palette[i % palette.length];
        return acc;
      },
      {} as Record<string, string>,
    );
  }, [participants]);

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <Button
          onClick={() => setMonthOffset((prev) => prev - 1)}
          className="px-2 py-1 text-sm rounded hover:bg-gray-100"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </Button>
        <div className="text-lg font-semibold text-center">
          {viewMonth.format("MMMM YYYY")}
        </div>
        <Button
          onClick={() => setMonthOffset((prev) => prev + 1)}
          className="px-2 py-1 text-sm rounded hover:bg-gray-100"
        >
          <ArrowRightIcon className="w-4 h-4" />
        </Button>
      </div>

      {/* Weekday labels */}
      <div className="grid grid-cols-7 text-xs font-medium text-center text-gray-500">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* Calendar grid */}
      {weeks.map((week, i) => (
        <div key={i} className="grid grid-cols-7 gap-1 text-sm">
          {week.map((entry) => {
            const isToday = moment().isSame(entry.date, "day");
            const inSchedule = isWithinSchedule(entry.date);
            const isEmpty = !inSchedule || !entry.participantId;

            return (
              <div
                key={entry.date.format("YYYY-MM-DD")}
                className={clsx(
                  "flex flex-col items-center p-1 border rounded min-h-[80px]",
                  isToday && inSchedule && "ring-2 ring-blue-500",
                  !inSchedule
                    ? "bg-gray-100 text-gray-400 opacity-50"
                    : isEmpty
                      ? "bg-gray-50 text-gray-300"
                      : (participantColorMap[entry.participantId!] ??
                        "bg-white"),
                )}
              >
                <div className="text-xs text-gray-500">
                  {entry.date.format("D")}
                </div>
                <div
                  className={clsx(
                    "mt-1 rounded-full w-8 h-8 flex items-center justify-center text-xs font-semibold",
                    entry.participantId
                      ? "bg-gray-100 text-gray-800"
                      : "bg-gray-50 text-gray-300",
                  )}
                >
                  {getParticipantInitials(entry.participantId)}
                </div>
                {entry.time && inSchedule && (
                  <div className="mt-1 text-[11px] text-blue-500">
                    {moment(entry.time, "HH:mm").format("h:mm A")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};
